import React, { useRef, useState, useCallback, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/frontend-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const DEBOUNCE_MS = 400;

/**
 * Masked text field for the "Deploy to Pivotree Launchpad" template's
 * Secrets & config step. Reports an opaque one-time staging token as its
 * RJSF value - never the real secret - by POSTing the real value to a
 * dedicated backend endpoint (launchpad-secrets-staging) as the user types.
 *
 * Why not Backstage's own built-in `ui:field: Secret`: that widget also
 * keeps the real value out of the form's reported value, but only within
 * the browser session, via a `secrets` context meant to be read with
 * `${{ secrets['key'] }}` in a template step - which does not support this
 * form's dynamic, user-named array of secrets (the context is keyed by
 * field path, not by the name the user typed). `ui:widget: password`
 * submits the real value as a normal form value, which Backstage's own
 * scaffolder persists indefinitely as part of the task's spec (a separate
 * `secrets` column IS scrubbed on task completion; the `spec` column is
 * not) - see `packages/backend/src/lib/secretsStaging.ts` for the full
 * writeup. This field is the alternative that keeps the array's UX while
 * keeping the real value out of that persisted spec entirely.
 */
export const SecretStagingField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, schema, required, disabled, rawErrors, formData } = props;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // One token per field instance, generated client-side, reused across
  // every edit so re-typing just overwrites the same staged entry.
  const tokenRef = useRef<string>(
    formData && formData.length > 0 ? formData : crypto.randomUUID(),
  );
  const [localValue, setLocalValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const stage = useCallback(async (value: string) => {
    const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
    await fetchApi.fetch(`${baseUrl}/launchpad-secrets-staging/stage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: tokenRef.current, value }),
    });
  }, [discoveryApi, fetchApi]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalValue(value);

    if (!value) {
      // Nothing staged for an empty field - report no value at all rather
      // than a token pointing at an empty string.
      onChange(undefined);
      return;
    }

    onChange(tokenRef.current);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      stage(value).catch(() => {
        // Surfaced as a validation error via rawErrors below on next render
        // would require lifting state; a console warning is enough here -
        // the user sees the field still populated and can retype/resubmit,
        // and the action-side resolution failure is a clear, actionable
        // error if a stage call silently failed.
        // eslint-disable-next-line no-console
        console.warn('Failed to stage secret value; it may not be saved correctly.');
      });
    }, DEBOUNCE_MS);
  };

  return (
    <TextField
      label={schema.title}
      required={required}
      disabled={disabled}
      error={Boolean(rawErrors?.length)}
      helperText={rawErrors?.length ? rawErrors.join(', ') : undefined}
      type="password"
      autoComplete="new-password"
      fullWidth
      value={localValue}
      onChange={handleChange}
    />
  );
};
